import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCopy, Download, Eraser, ListPlus, Search, Loader2, ExternalLink } from "lucide-react";

import { appConfig } from "@/config";

const API_BASE = appConfig.apiUrl || (typeof window !== "undefined" && window.SERPBACKLINK_API) || process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;
const MAX = 50;

const SAMPLE_URLS = [
  "google.com",
  "github.com",
  "wikipedia.org",
  "moz.com",
  "example-spam-site-999.xyz",
];

const GRADE_STYLES = {
  strong: "text-[#4ADE80] bg-[#4ADE80]/[0.12] border-[#4ADE80]/30",
  average: "text-[#FBBF24] bg-[#FBBF24]/[0.12] border-[#FBBF24]/30",
  weak: "text-[#F87171] bg-[#F87171]/[0.12] border-[#F87171]/30",
};

const scoreColor = (v) =>
  v >= 60 ? "text-[#4ADE80]" : v >= 30 ? "text-[#FBBF24]" : "text-[#F87171]";
const spamColor = (v) =>
  v < 5 ? "text-[#4ADE80]" : v <= 30 ? "text-[#FBBF24]" : "text-[#F87171]";

export const Checker = () => {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [invalid, setInvalid] = useState([]);
  const [mode, setMode] = useState(null);
  const [filter, setFilter] = useState("");

  const urls = useMemo(
    () => input.split("\n").map((l) => l.trim()).filter(Boolean),
    [input]
  );
  const overLimit = urls.length > MAX;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return results;
    return results.filter((r) => r.url.toLowerCase().includes(q));
  }, [results, filter]);

  const runCheck = async () => {
    if (!urls.length) {
      toast.error("Paste at least one URL first");
      return;
    }
    if (overLimit) {
      toast.error(`Maximum ${MAX} URLs per check — you have ${urls.length}`);
      return;
    }
    setLoading(true);
    setResults([]);
    setInvalid([]);
    try {
      const { data } = await axios.post(`${API}/check`, { urls });
      setMode(data.mode);
      setInvalid(data.invalid || []);
      const rows = data.results || [];
      for (let i = 0; i < rows.length; i++) {
        await new Promise((r) => setTimeout(r, i === 0 ? 250 : 140));
        setResults((prev) => [...prev, rows[i]]);
      }
      toast.success(`${rows.length} URL${rows.length > 1 ? "s" : ""} analyzed`);
      if (data.invalid?.length) {
        toast.warning(`${data.invalid.length} invalid line${data.invalid.length > 1 ? "s" : ""} skipped`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed — try again");
    } finally {
      setLoading(false);
    }
  };

  const copyAll = async () => {
    const tsv = ["URL\tDA\tPA\tSpam Score\tGrade"]
      .concat(results.map((r) => `${r.url}\t${r.domain_authority}\t${r.page_authority}\t${r.spam_score}%\t${r.grade}`))
      .join("\n");
    await navigator.clipboard.writeText(tsv);
    toast.success("Results copied to clipboard");
  };

  const exportCsv = () => {
    const csv = ["URL,Domain,DA,PA,Spam Score,Grade,Status"]
      .concat(results.map((r) => `"${r.url}",${r.domain},${r.domain_authority},${r.page_authority},${r.spam_score},${r.grade},"${r.status}"`))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "serpbacklink-report.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  return (
    <section id="checker" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-4">// The Tool</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F3F4F6]">
            Paste up to 50 URLs. Get the truth.
          </h2>
        </motion.div>

        <div
          data-testid="checker-widget-container"
          className="mt-10 border border-[#263248] bg-[#121824] rounded-xl overflow-hidden"
        >
          <div className="p-5 sm:p-8 border-b border-[#263248]">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <button
                  data-testid="checker-load-sample-button"
                  onClick={() => setInput(SAMPLE_URLS.join("\n"))}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#263248] text-xs font-mono2 text-[#9CA3AF] hover:border-[#D4FF00] hover:text-[#D4FF00] transition-colors"
                >
                  <ListPlus size={14} /> Load Sample URLs
                </button>
                <button
                  data-testid="checker-clear-button"
                  onClick={() => { setInput(""); setResults([]); setInvalid([]); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#263248] text-xs font-mono2 text-[#9CA3AF] hover:border-[#F87171] hover:text-[#F87171] transition-colors"
                >
                  <Eraser size={14} /> Clear All
                </button>
              </div>
              <span
                data-testid="checker-url-count-indicator"
                className={`font-mono2 text-xs ${overLimit ? "text-[#F87171]" : "text-[#6B7280]"}`}
              >
                {urls.length}/{MAX} URLs
              </span>
            </div>

            <textarea
              data-testid="checker-url-input-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={7}
              spellCheck={false}
              placeholder={"Paste URLs, one per line\ngoogle.com\nhttps://moz.com/blog\nwikipedia.org"}
              className="w-full rounded-lg bg-[#0B0F17] border border-[#263248] focus:border-[#D4FF00] focus:outline-none focus:ring-1 focus:ring-[#D4FF00] px-4 py-3 font-mono2 text-sm text-[#F3F4F6] placeholder:text-[#4B5563] resize-y transition-colors"
            />

            <div className="mt-5 flex flex-wrap items-center gap-4">
              <button
                data-testid="checker-submit-analyze-button"
                onClick={runCheck}
                disabled={loading || !urls.length || overLimit}
                className="flex items-center gap-2 px-8 py-3.5 rounded-md bg-[#D4FF00] text-[#0B0F17] font-bold text-base hover:bg-[#E5FF66] active:bg-[#BCE600] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin-slow" data-testid="checker-loading-spinner" />
                    Analyzing...
                  </>
                ) : (
                  <>Analyze {urls.length > 0 ? `${Math.min(urls.length, MAX)} URL${urls.length > 1 ? "s" : ""}` : "URLs"}</>
                )}
              </button>
              {mode === "mock" && results.length > 0 && (
                <span data-testid="checker-demo-mode-badge" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#FBBF24] border border-[#FBBF24]/30 bg-[#FBBF24]/10 px-2.5 py-1 rounded">
                  Demo data — MOZ key not connected
                </span>
              )}
            </div>
          </div>

          {(results.length > 0 || loading) && (
            <div className="p-5 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                  <input
                    data-testid="checker-filter-results-input"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter by URL..."
                    className="pl-9 pr-3 py-2 rounded-md bg-[#0B0F17] border border-[#263248] focus:border-[#D4FF00] focus:outline-none font-mono2 text-xs text-[#F3F4F6] placeholder:text-[#4B5563] w-56 transition-colors"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="checker-copy-clipboard-button"
                    onClick={copyAll}
                    disabled={!results.length}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-[#263248] text-xs font-semibold text-[#9CA3AF] hover:border-[#D4FF00] hover:text-[#D4FF00] transition-colors disabled:opacity-40"
                  >
                    <ClipboardCopy size={14} /> Copy All Data
                  </button>
                  <button
                    data-testid="checker-export-csv-button"
                    onClick={exportCsv}
                    disabled={!results.length}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-[#263248] text-xs font-semibold text-[#9CA3AF] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors disabled:opacity-40"
                  >
                    <Download size={14} /> Download CSV
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-[#263248]">
                <table data-testid="checker-results-table" className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="bg-[#0B0F17] font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#6B7280] text-left">
                      <th className="px-4 py-3 w-10">#</th>
                      <th className="px-4 py-3">URL</th>
                      <th className="px-4 py-3">DA</th>
                      <th className="px-4 py-3">PA</th>
                      <th className="px-4 py-3">Spam</th>
                      <th className="px-4 py-3">Rating</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {filtered.map((r, i) => (
                        <motion.tr
                          key={r.domain}
                          data-testid={`checker-results-row-${i}`}
                          initial={{ opacity: 0, x: -14 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.35 }}
                          className="border-t border-[#263248] hover:bg-[#1A2234] transition-colors"
                        >
                          <td className="px-4 py-3 font-mono2 text-xs text-[#6B7280]">{i + 1}</td>
                          <td className="px-4 py-3 font-mono2 text-sm text-[#F3F4F6] max-w-[280px] truncate">
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4FF00] transition-colors inline-flex items-center gap-1.5">
                              {r.url.replace(/^https?:\/\//, "")}
                              <ExternalLink size={11} className="text-[#6B7280]" />
                            </a>
                          </td>
                          <td className={`px-4 py-3 font-mono2 text-base font-bold ${scoreColor(r.domain_authority)}`}>{r.domain_authority}</td>
                          <td className={`px-4 py-3 font-mono2 text-base font-bold ${scoreColor(r.page_authority)}`}>{r.page_authority}</td>
                          <td className={`px-4 py-3 font-mono2 text-base font-bold ${spamColor(r.spam_score)}`}>{r.spam_score}%</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2.5 py-0.5 rounded-full border text-[10px] font-mono2 uppercase tracking-wider ${GRADE_STYLES[r.grade]}`}>
                              {r.grade}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono2 text-xs text-[#4ADE80]">{r.status}</td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
                {loading && (
                  <div className="px-4 py-4 font-mono2 text-xs text-[#6B7280] border-t border-[#263248] flex items-center gap-2">
                    <Loader2 size={13} className="animate-spin-slow text-[#D4FF00]" />
                    Querying index... {results.length}/{Math.min(urls.length, MAX)}
                  </div>
                )}
              </div>

              {invalid.length > 0 && (
                <div data-testid="checker-invalid-lines" className="mt-4 font-mono2 text-xs text-[#F87171]">
                  Skipped invalid: {invalid.join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
